// Announcements, events and their targeting.
//
// Wire shapes here are snake_case on purpose: they are exactly what the API
// returns, and apps/coach and apps/runner consume them raw. apps/mobile keeps
// its own snake -> camel mapping in src/lib/announcements.ts, matching how
// src/lib/zoomReservations.ts already handles zoom_reservations.

import { COACH_ROLES, UserRole } from './user';

/**
 * Who a published announcement or event is for.
 *
 *   everyone      every authenticated account
 *   all_coaches   COACH_ROLES = coach | hybrid | admin | lord
 *   all_runners   runner | hybrid
 *   roles         filter.roles, an explicit list
 *   segment       filter over runner_season_info (season / fs_cohort /
 *                 race_distance) — the only place segmentation actually lives
 *
 * Keep in sync with AUDIENCE_KINDS in
 * cloud-run/qual-scores-api/lib/audience.js (can't share code across the
 * TS/CommonJS boundary) and with the audience_kind CHECK constraint in
 * cloud-sql/2026-09-12-announcements-events-push.sql;
 * audience-constants.test.js asserts the first two stay aligned.
 */
export type AudienceKind = 'everyone' | 'all_coaches' | 'all_runners' | 'roles' | 'segment';

export const AUDIENCE_KINDS: AudienceKind[] = [
  'everyone',
  'all_coaches',
  'all_runners',
  'roles',
  'segment',
];

/**
 * The subset the API will actually create today. The others exist in the type
 * and the CHECK constraint so adding them later is additive, but creating one
 * now would produce an announcement nobody can see — audienceVisibleTo has no
 * arm for 'segment', and refusing at write time is far kinder than a silent
 * empty audience. Twin: IMPLEMENTED_AUDIENCE_KINDS in lib/audience.js.
 */
export const IMPLEMENTED_AUDIENCE_KINDS: AudienceKind[] = ['everyone', 'all_coaches'];

export interface AudienceFilter {
  roles?: string[];
  season?: string;
  fs_cohort?: string[];
  race_distance?: string[];
}

// Same case-folding rationale as normalize() in ./user — callers hold role
// strings from caches, fixtures and future sources, not just from v_rhwb_roles.
const normalize = (role?: UserRole | string | null): string =>
  typeof role === 'string' ? role.toLowerCase() : '';

/**
 * Does a person with this role see content carrying this audience?
 *
 * Pure, and the exact twin of audienceVisibleTo in
 * cloud-run/qual-scores-api/lib/audience.js — which in turn is the twin of the
 * SQL in VISIBILITY_SQL. Three copies of one visibility rule is unusual, so
 * audience-constants.test.js pins the two JS ones against each other and
 * against isCoachRole.
 *
 * 'segment' always returns false: resolving it needs a runner_season_info join
 * that only exists server-side, so a client must never guess at it.
 */
export const audienceVisibleTo = (
  kind: AudienceKind | string,
  role?: UserRole | string | null,
  filter: AudienceFilter = {}
): boolean => {
  const r = normalize(role);
  if (!r) return false;
  switch (kind) {
    case 'everyone':
      return true;
    case 'all_coaches':
      return (COACH_ROLES as string[]).includes(r);
    case 'all_runners':
      return r === 'runner' || r === 'hybrid';
    case 'roles':
      return (filter.roles ?? []).map((x) => x.toLowerCase()).includes(r);
    default:
      return false;
  }
};

export const audienceLabel = (kind: AudienceKind | string): string => {
  switch (kind) {
    case 'everyone':
      return 'Everyone';
    case 'all_coaches':
      return 'All coaches';
    case 'all_runners':
      return 'All runners';
    case 'roles':
      return 'Selected roles';
    case 'segment':
      return 'Selected cohorts';
    default:
      return String(kind);
  }
};

export type PublishStatus = 'draft' | 'published' | 'archived';

export type EventType = 'milestone' | 'meeting' | 'race' | 'training' | 'other';

export interface AnnouncementRow {
  id: string;
  title: string;
  body: string;
  event_id: string | null;
  audience_kind: AudienceKind;
  audience_filter: AudienceFilter;
  status: PublishStatus;
  published_at: string | null;
  expires_at: string | null;
  pinned: boolean;
  requires_ack: boolean;
  created_by_email: string;
  created_by_name: string | null;
  created_at: string;
  /** Present on feed/detail rows only — the caller's own state, not a global. */
  read_at?: string | null;
  acknowledged_at?: string | null;
  /** Present on /api/comms rows only. */
  push_sent_at?: string | null;
  push_device_count?: number | null;
  read_count?: number;
  ack_count?: number;
}

export interface EventRow {
  id: string;
  title: string;
  description: string | null;
  event_type: EventType;
  starts_at: string;
  ends_at: string | null;
  all_day: boolean;
  location: string | null;
  url: string | null;
  audience_kind: AudienceKind;
  audience_filter: AudienceFilter;
  status: PublishStatus;
  published_at: string | null;
  created_by_email: string;
  created_by_name: string | null;
  created_at: string;
}

export interface AnnouncementFeedResponse {
  data: AnnouncementRow[];
  unread_count: number;
  version: string | null;
  unchanged?: boolean;
}
